export const DATE_OF_BIRTH = 'date_of_birth';

// TODO: consolidate usdCurrency and usdCurrencyNoZero into one function and change interface to accept an options object.
// removing cents is confurable using Intl.NumberFormat options ({trailingZeroDisplay: 'stripIfInteger'})
// see https://stashinvest.atlassian.net/browse/WEB-750
export const usdCurrency = (
	value: string | number | null | undefined,
	cents: boolean = true,
	showPlusSign: boolean = false,
): string => {
	const floatValue =
		typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;

	return Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: cents ? 2 : 0,
		maximumFractionDigits: cents ? 2 : 0,
		signDisplay: showPlusSign ? 'always' : 'auto',
		// Pass nullish values straight through to preserve legacy formatting
		// ($0.00 for null, $NaN for undefined) that existing callers rely on.
	}).format(floatValue as number);
};

export const usdCurrencyNoZero = (value: string | number | null | undefined): string => {
	return usdCurrency(value).replace('.00', '');
};

export const stringToNumberConverter = (value: string): number => {
	const numberString = value.replace(/[^0-9.-]/g, '');
	return parseFloat(numberString);
};

export const formatInputCurrency = (value: string): string => {
	// remove all non-numerical values except for . and -
	const numberString = value.replace(/[^0-9.-]/g, '');

	// Bail out only when there are no digits to format. We must NOT bail on
	// `parseFloat(numberString) === 0` here, because that strips the leading
	// `0` while the user is mid-typing values like `0.5` (the next keystroke
	// then sees an empty input and the decimal portion never lands).
	if (!/\d/.test(numberString)) return '$';

	const num = parseFloat(numberString);

	// we need to keep the trailing decimal for typing purposes
	const decimalPlace = numberString.split('.')[1];
	const suffix = decimalPlace !== undefined && !decimalPlace.length ? '.' : '';

	// round to 2 decimal places only if needed
	const roundedNum = Math.round(num * 100) / 100;

	// return currency formatted
	return '$' + roundedNum.toLocaleString('en-US') + suffix;
};

type DateOfBirthParts = {
	birth_month: string;
	birth_day: string;
	birth_year: string;
};

export const getDOBFromString = (dob: string): DateOfBirthParts => {
	if (typeof dob !== 'string') {
		throw new Error('A non-string argument was passed to getDOBFromString Function');
	}

	const parts = dob.replace(/\s+/g, '').split('-');
	const birth_year = parts[0];
	const birth_month = parts[1];
	const birth_day = parts[2];

	return {
		birth_month,
		birth_day,
		birth_year,
	};
};

export const formatDOB = (year: string, month: string, day: string): string => {
	if (typeof year !== 'string' || typeof month !== 'string' || typeof day !== 'string') {
		throw new Error('A non-string argument was passed to formatDOB Function');
	}

	return `${year}-${month}-${day}`;
};

export const trimFirstPhoneDigit = (number: string): string => {
	if (!number) {
		return '';
	}
	return number.slice(1);
};

export const formatSpaceAfterCertainNumber = (
	charactersToSpace: number,
): ((string: string) => string) => {
	if (typeof charactersToSpace !== 'number') throw new Error('Please enter a number');

	return function (string: string): string {
		if (typeof string !== 'string') throw new Error('Please enter a string');
		const regex = new RegExp(`\\d{${charactersToSpace}}`, 'g');

		return string.replace(regex, (match) => match + ' ');
	};
};

export const format4Spaces = formatSpaceAfterCertainNumber(4);
export const format3Spaces = formatSpaceAfterCertainNumber(3);

// Live-input mask for a 16-digit card number. Strips non-digits, caps at 16
// digits, groups into "1234 5678 9012 3456", and trims the trailing space that
// format4Spaces leaves after a complete group.
export const formatCardNumber = (value: string): string =>
	format4Spaces(value.replace(/\D/g, '').slice(0, 16)).trim();

// Live-input mask for an MM/YY expiry. Strips non-digits, caps at 4 digits, and
// inserts the "/" once the month is complete.
export const formatExpiry = (value: string): string => {
	const digits = value.replace(/\D/g, '').slice(0, 4);
	return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

// Live-input mask for an MM/DD/YYYY date of birth. Strips non-digits, caps at 8
// digits, and inserts "/" separators as the month and day complete.
export const formatDateOfBirthInput = (value: string): string => {
	const digits = value.replace(/\D/g, '').slice(0, 8);
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const getReadableValue = (number: unknown, hideCurrency?: boolean): string => {
	if (typeof number !== 'number') return 'N/A';
	const absoluteValue = Math.abs(Number(number));
	const prefix = hideCurrency ? '' : '$';
	const trillions = absoluteValue / 1.0e12;
	const billions = absoluteValue / 1.0e9;
	const millions = absoluteValue / 1.0e6;
	const thousands = absoluteValue / 1.0e3;

	return absoluteValue >= 1.0e12
		? prefix + trillions.toFixed(1) + ' trillion'
		: absoluteValue >= 1.0e9
		? prefix + billions.toFixed(1) + ' billion'
		: absoluteValue >= 1.0e6
		? prefix + millions.toFixed(1) + ' million'
		: absoluteValue >= 1.0e3
		? prefix + thousands.toFixed(1) + ' thousand'
		: prefix + absoluteValue;
};

export const capitalizeFirstLetter = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

type CurrencyWithSeparatedDecimals = {
	dollarSign: string; // "$" (or "-$" when the value is negative)
	whole: string; // e.g. "53,770"
	decimal: string; // e.g. ".45"
};

// Intl.NumberFormat with `maximumFractionDigits: 2` handles values that
// would otherwise flip to exponential notation via `Number.toFixed` (which
// switches at ~1e21). It also emits the same "en-US" thousands separators
// we want, so we can split the formatted string directly.
const currencySplitFormatter = new Intl.NumberFormat('en-US', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const formatCurrencyWithSeparatedDecimals = (
	value: number,
): CurrencyWithSeparatedDecimals => {
	if (!Number.isFinite(value)) return { dollarSign: '$', whole: '-', decimal: '.--' };

	const isNegative = value < 0;
	const absoluteValue = Math.abs(value);
	const formatted = currencySplitFormatter.format(absoluteValue);
	const decimalIndex = formatted.lastIndexOf('.');
	const whole = decimalIndex === -1 ? formatted : formatted.slice(0, decimalIndex);
	const decimal = decimalIndex === -1 ? '.00' : formatted.slice(decimalIndex);
	return {
		dollarSign: isNegative ? '-$' : '$',
		whole,
		decimal,
	};
};
